import React from 'react';
import type { WorkspaceStatus } from '../../flux/stores/AuthStore';

interface SyncStatusBadgeProps {
   workspaceStatus: WorkspaceStatus;
   isOnline: boolean;
   exportStatus: 'idle' | 'exporting' | 'success' | 'error';
   isSyncBlocked?: boolean;
   syncBlockedMessage?: string | null;
   isSyncing?: boolean;
}

function SyncStatusBadge({
   workspaceStatus,
   isOnline,
   exportStatus,
   isSyncBlocked,
   syncBlockedMessage,
   isSyncing,
}: SyncStatusBadgeProps) {
   return (
      <>
         {workspaceStatus === 'READY' && isOnline && isSyncBlocked && (
            <div
               className="no-drag flex items-center px-2 py-1 rounded text-[11px] font-mono text-rose-300 bg-rose-500/10"
               title={syncBlockedMessage || 'Sync blocked'}
            >
               <span className="w-2 h-2 rounded-full mr-2 bg-rose-400" />
               Sync Blocked
            </div>
         )}

         {workspaceStatus === 'READY' && isOnline && !isSyncBlocked && isSyncing && (
            <div
               className="no-drag flex items-center px-2 py-1 rounded text-[11px] font-mono text-emerald-300 bg-emerald-500/10"
               title="Syncing changes"
            >
               <span className="w-2 h-2 rounded-full mr-2 bg-emerald-400 animate-pulse" />
               Syncing
            </div>
         )}

         {workspaceStatus === 'READY' && isOnline && !isSyncBlocked && !isSyncing && (
            <div
               className="no-drag flex items-center px-2 py-1 rounded text-[11px] font-mono text-emerald-300 bg-emerald-500/10"
               title="Connected"
            >
               <span className="w-2 h-2 rounded-full mr-2 bg-emerald-400" />
               Online
            </div>
         )}

         {workspaceStatus === 'PROVISIONING' && (
            <div
               className="no-drag flex items-center px-3 py-1.5 rounded text-[11px] font-mono text-amber-100 bg-amber-500/20 border border-amber-400/30 shadow-sm shadow-amber-500/20"
               title="Setting up your workspace..."
            >
               <span className="w-2.5 h-2.5 rounded-full mr-2 bg-amber-300 animate-pulse" />
               Preparing workspace...
            </div>
         )}

         {workspaceStatus === 'AUTHENTICATED' && (
            <div
               className="no-drag flex items-center px-3 py-1.5 rounded text-[11px] font-mono text-blue-100 bg-blue-500/20 border border-blue-400/30"
               title="Connecting to server..."
            >
               <span className="w-2.5 h-2.5 rounded-full mr-2 bg-blue-300 animate-pulse" />
               Connecting...
            </div>
         )}

         {!isOnline && (
            <div
               className="no-drag flex items-center px-3 py-1.5 rounded text-[11px] font-mono text-amber-200 bg-amber-500/10"
               title="Internet connection required"
            >
               <span className="w-2.5 h-2.5 rounded-full mr-2 bg-amber-400" />
               Offline
            </div>
         )}

         {/* Export status */}
         {exportStatus === 'exporting' && (
            <div className="no-drag flex items-center px-2 py-1 rounded text-[11px] font-mono text-blue-300 bg-blue-500/10">
               <span className="w-2 h-2 rounded-full mr-2 bg-blue-400 animate-pulse" />
               Exporting...
            </div>
         )}
         {exportStatus === 'success' && (
            <div className="no-drag flex items-center px-2 py-1 rounded text-[11px] font-mono text-emerald-300 bg-emerald-500/10">
               <span className="w-2 h-2 rounded-full mr-2 bg-emerald-400" />
               Exported
            </div>
         )}
         {exportStatus === 'error' && (
            <div className="no-drag flex items-center px-2 py-1 rounded text-[11px] font-mono text-rose bg-rose/20">
               <span className="w-2 h-2 rounded-full mr-2 bg-rose" />
               Export Failed
            </div>
         )}
      </>
   );
}

export default SyncStatusBadge;
